import { useState, useMemo } from 'react';
import type {
  ChargePointCluster,
  ChargePointWrapper,
} from '@/features/charge-points/types/charge-point.types';
import { StateBreakdownTooltip } from './StateBreakdownTooltip';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { getContrastingTextColor } from '@/utils/color-utils';
import {
  getConicGradient,
  countColorsTotal,
  colorsFromPinColor,
} from '../utils/marker-style';
import { useMarkerCustomization } from '../hooks/useMarkerCustomization';
import { ClusterBubble } from './ClusterBubble';
import {
  clusterAvailableShare,
  clusterTotal,
  type ClusterStyle,
} from '../utils/cluster-layout';

interface ClusterMarkerProps {
  cluster: ChargePointCluster;
  showOperatorIcon?: boolean;
  highlightColor?: string;
  highlightIconUrl?: string;
  overlapCount?: number;
  overlapGroup?: (ChargePointWrapper | ChargePointCluster)[];
  /** Biggest cluster total in view, for size scaling */
  maxCount?: number;
  /** Largest bubble that fits the grid on screen */
  maxPx?: number;
  /** New cluster look; null = the legacy status ring */
  clusterStyle?: ClusterStyle | null;
}

/**
 * Cluster marker for grouped charge points
 */
export function ClusterMarker({
  cluster,
  showOperatorIcon = false,
  highlightColor = '#302ee0', // Default to Monta blue
  highlightIconUrl,
  overlapCount,
  overlapGroup,
  maxCount,
  maxPx,
  clusterStyle = null,
}: ClusterMarkerProps) {
  const { count, pin } = cluster;
  const [isHovered, setIsHovered] = useState(false);
  const hasOverlaps = overlapCount && overlapCount > 1;

  // Get operator theme and marker style
  const operatorTheme = useOperatorStore((s) => s.theme);
  const markerStyle = useFilterStore((s) => s.markerStyle);

  // Marker customization from marker_* URL params (no-op when unset)
  const { statusColors, markerColor, markerSize } = useMarkerCustomization();

  // Calculate combined data from all overlapping markers
  const combinedData = useMemo(() => {
    if (!hasOverlaps || !overlapGroup || overlapGroup.length === 0) {
      // No overlaps, use single cluster data.
      // Use pin.number (total charge points) not count (number of pins inside).
      // Fall back to the dominant pin.color when the backend sent no per-status
      // breakdown, so the ring still renders its status color (and any custom
      // cluster colors) instead of a plain black marker.
      const colors =
        countColorsTotal(pin?.colors) > 0
          ? pin?.colors
          : colorsFromPinColor(pin?.color);
      return {
        colors,
        totalCount: pin?.number || count,
      };
    }

    // Calculate combined data from all overlapping markers
    let totalGreen = 0;
    let totalGrey = 0;
    let totalOrange = 0;
    let totalRed = 0;
    let totalBlue = 0;
    let totalCount = 0;

    overlapGroup.forEach((item) => {
      if ('count' in item) {
        // It's a cluster - use pin.number (total charge points) not count (number of pins)
        totalCount += item.pin?.number || item.count;
        if (item.pin?.colors) {
          totalGreen += item.pin.colors.green || 0;
          totalGrey +=
            (item.pin.colors.grey || 0) + (item.pin.colors.gray || 0);
          totalOrange += item.pin.colors.orange || 0;
          totalRed += item.pin.colors.red || 0;
          totalBlue += item.pin.colors.blue || 0;
        }
      } else {
        // It's a charge point wrapper
        const cpPin = item.pin;
        if (item.type === 'chargePointGroup' && cpPin.colors) {
          const cpTotal =
            (cpPin.colors.green || 0) +
            ((cpPin.colors.grey || 0) + (cpPin.colors.gray || 0)) +
            (cpPin.colors.orange || 0) +
            (cpPin.colors.red || 0) +
            (cpPin.colors.blue || 0);
          totalCount += cpTotal;
          totalGreen += cpPin.colors.green || 0;
          totalGrey += (cpPin.colors.grey || 0) + (cpPin.colors.gray || 0);
          totalOrange += cpPin.colors.orange || 0;
          totalRed += cpPin.colors.red || 0;
          totalBlue += cpPin.colors.blue || 0;
        } else {
          totalCount += 1;
          // Single charge point - add to appropriate color based on pin color
          if (cpPin.color === 'green') totalGreen += 1;
          else if (cpPin.color === 'orange') totalOrange += 1;
          else if (cpPin.color === 'red') totalRed += 1;
          else totalGrey += 1;
        }
      }
    });

    return {
      colors: {
        green: totalGreen,
        grey: totalGrey,
        orange: totalOrange,
        red: totalRed,
        blue: totalBlue,
      },
      totalCount,
    };
  }, [hasOverlaps, overlapGroup, count, pin]);

  // Use combined count for display
  const displayNumber = combinedData.totalCount;

  // Determine size: honor an explicit marker_size override, otherwise size by count
  const getSize = () => {
    if (markerSize === 'xs') return 'w-8 h-8 text-[10px]';
    if (markerSize === 's') return 'w-10 h-10 text-xs';
    // 'm' or absent → count-based sizing (unchanged)
    if (displayNumber >= 500) return 'w-16 h-16 text-lg';
    if (displayNumber >= 100) return 'w-14 h-14 text-base';
    if (displayNumber >= 50) return 'w-12 h-12 text-sm';
    return 'w-11 h-11 text-sm';
  };

  // Get display count with threshold
  // Clusters ALWAYS show "+" suffix to distinguish from charge point pins
  const getDisplayCount = () => {
    if (displayNumber >= 500) return '500+';
    if (displayNumber >= 100) return '100+';
    if (displayNumber >= 50) return '50+';
    if (displayNumber >= 20) return '20+';
    if (displayNumber >= 10) return '10+';
    // Always add "+" for clusters, even for small numbers
    return `${displayNumber}+`;
  };

  const sizeClass = getSize();
  const displayCount = getDisplayCount();

  // Check if we have segmented colors from combined data
  const conicGradient = combinedData.colors
    ? getConicGradient(combinedData.colors, statusColors)
    : null;
  const borderWidth = '4px';

  // Use segmented colors if available, otherwise default to black
  const colorClass = conicGradient ? '' : 'bg-monta-black border-monta-black';

  // Calculate inner circle styles based on marker style
  const innerCircleStyle = useMemo(() => {
    // Top priority: explicit marker_color accent (overrides marker_style center)
    if (markerColor) {
      return {
        backgroundColor: markerColor,
        color: getContrastingTextColor(markerColor),
      };
    }
    if (markerStyle === 'operator' && operatorTheme?.primary_color) {
      const bgColor = operatorTheme.primary_color;
      const textColor = getContrastingTextColor(bgColor);
      return {
        backgroundColor: bgColor,
        color: textColor,
      };
    }
    if (markerStyle === 'operator_neutral') {
      return {
        backgroundColor: 'rgb(var(--c-neutral))',
        color: 'rgb(var(--c-ink))',
      };
    }
    // Default style: theme surface + ink (white/dark in light, dark/white in dark)
    return {
      backgroundColor: 'rgb(var(--c-surface))',
      color: 'rgb(var(--c-ink))',
    };
  }, [markerColor, markerStyle, operatorTheme]);

  if (clusterStyle) {
    const brandColor =
      markerColor ??
      (markerStyle === 'operator' ? operatorTheme?.primary_color : undefined);
    return (
      <ClusterBubble
        count={clusterTotal(cluster)}
        maxCount={maxCount ?? clusterTotal(cluster)}
        maxPx={maxPx}
        variant={clusterStyle}
        brandColor={brandColor ?? undefined}
        availableShare={
          // Preview switch: ?cluster_bar=0 hides the availability bar
          new URLSearchParams(window.location.search).get('cluster_bar') === '0'
            ? undefined
            : clusterAvailableShare(cluster)
        }
      />
    );
  }

  return (
    <div className="relative">
      {/* Tooltip on hover for all clusters - shows aggregated data */}
      {isHovered && (
        <StateBreakdownTooltip
          colors={combinedData.colors}
          totalCount={combinedData.totalCount}
        />
      )}

      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${sizeClass} ${colorClass} ${conicGradient ? '' : 'border-4'} rounded-full flex items-center justify-center shadow-monta-lg hover:scale-110 transition-transform cursor-pointer`}
        style={
          conicGradient
            ? {
                background: `${conicGradient}`,
                padding: borderWidth,
                boxShadow:
                  '0 0 0 2px rgb(var(--c-surface) / 0.85), 0 8px 10px -2px rgba(0, 0, 0, 0.1)',
              }
            : {
                boxShadow:
                  '0 0 0 2px rgb(var(--c-surface) / 0.85), 0 8px 10px -2px rgba(0, 0, 0, 0.1)',
              }
        }
        aria-label={`Cluster with ${count} charge points`}
        role="button"
        tabIndex={0}
      >
        <div
          className="rounded-full w-full h-full flex items-center justify-center"
          style={innerCircleStyle}
        >
          <span className="font-bold" style={{ color: innerCircleStyle.color }}>
            {displayCount}
          </span>
        </div>
      </div>
      {/* Overlap count badge (bottom-right corner) */}
      {overlapCount && overlapCount > 1 && (
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center z-10 border-2 border-white shadow-md">
          <span className="text-white text-[9px] font-bold">
            ×{overlapCount}
          </span>
        </div>
      )}

      {/* Operator icon badge (top-right corner) - rendered AFTER button for z-index layering */}
      {showOperatorIcon && highlightIconUrl && (
        <div
          className="absolute -top-1 -right-1 flex items-center justify-center z-10"
          style={
            markerStyle === 'operator_neutral'
              ? { width: '20px', height: '20px' }
              : {
                  width: '20px',
                  height: '20px',
                  background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  border: `2px solid ${highlightColor}`,
                  borderRadius: '8px',
                }
          }
        >
          <img
            src={highlightIconUrl}
            alt="Operator icon"
            className="object-contain"
            style={
              markerStyle === 'operator_neutral'
                ? { width: '20px', height: '20px' }
                : { width: '12px', height: '12px' }
            }
          />
        </div>
      )}
    </div>
  );
}
